import { CODE_SYSTEMS, type TargetComparator } from '@liza/fhir-utils';

/**
 * System for in-house lifestyle-measurement codes — the result code a metric's Task/Goal/Observation
 * carries when no standard LOINC exists (see the lifestyle-change quantitative-targets design doc).
 * No catalog entry uses it yet: the metrics needing a minted code (workouts count, MVPA, floors
 * climbed, added sugar, awake/WASO duration, mindfulness sessions/time) are deferred until minted
 * (tracked in #5972), so the CodeSystem itself is not deployed yet. Kept as the canonical system id:
 * the result-code plumbing recognizes it and the deferred metrics — plus the CodeSystem defining
 * them — will land on it.
 */
export const LIFESTYLE_MEASUREMENTS_SYSTEM = 'https://liza.health/fhir/CodeSystem/lifestyle-measurements';

/** Which lifestyle domain a metric belongs to — drives grouping and the card/picker icon. Distinct
 * from the Task's `liza-task-category`: this is the metric's inherent domain, keyed by LOINC. */
export type LifestyleMeasurementDomain = 'movement' | 'food' | 'sleep' | 'mind';

// The comparator type is shared from @liza/fhir-utils so the catalog, services, tRPC schemas, and
// the picker all agree. There is no `=`; a catalog `=` target carries no comparator ("meet the target").
export type { TargetComparator };

export interface LifestyleMeasurementConfig {
  /** LOINC code, or a minted code on `LIFESTYLE_MEASUREMENTS_SYSTEM`. */
  code: string;
  system: string;
  /** Patient-friendly label shown in the picker and on the card. */
  display: string;
  /** Spanish metric label; rendered when the app language is Spanish. */
  displayEs: string;
  domain: LifestyleMeasurementDomain;
  /** Default comparator; omitted for catalog `=` targets. */
  comparator?: TargetComparator;
  /** Default target value (editable in the form). */
  target: number;
  /** Display unit label (Imperial-preferred; not editable — no unit-preference support yet). */
  unit: string;
  /** Spanish unit label (hand-curated; medical symbols like mg/mcg/g/Cal/L stay as-is, only the
   * time suffix and unit words translate — e.g. `steps/day` → `pasos/día`). */
  unitEs: string;
}

// Every metric with a LOINC code verified against loinc.org (nutrition incl. vitamins/minerals,
// plus sleep-stage durations); only the metrics with no standard code (workouts count, MVPA,
// floors, added sugar, WASO, mindfulness) remain deferred until minted, tracked in #5972.
// Targets/units are Imperial-preferred per #5862 (Distance mi/day, energy Cal/day, Water fl oz/day).
export const LIFESTYLE_MEASUREMENT_CONFIGS: LifestyleMeasurementConfig[] = [
  // Physical activity
  {
    code: '41950-7',
    system: CODE_SYSTEMS.LOINC,
    display: 'Steps',
    displayEs: 'Pasos',
    domain: 'movement',
    comparator: '>=',
    target: 10000,
    unit: 'steps/day',
    unitEs: 'pasos/día',
  },
  {
    code: '41953-1',
    system: CODE_SYSTEMS.LOINC,
    display: 'Distance covered',
    displayEs: 'Distancia recorrida',
    domain: 'movement',
    comparator: '>=',
    target: 3,
    unit: 'mi/day',
    unitEs: 'mi/día',
  },
  {
    code: '41979-6',
    system: CODE_SYSTEMS.LOINC,
    display: 'Total energy burned (active)',
    displayEs: 'Energía total quemada (activa)',
    domain: 'movement',
    comparator: '>=',
    target: 500,
    unit: 'Cal/day',
    unitEs: 'Cal/día',
  },

  // Nutrition
  {
    code: '75302-0',
    system: CODE_SYSTEMS.LOINC,
    display: 'Total energy consumed',
    displayEs: 'Energía total consumida',
    domain: 'food',
    target: 2000,
    unit: 'Cal/day',
    unitEs: 'Cal/día',
  },
  {
    code: '9065-4',
    system: CODE_SYSTEMS.LOINC,
    display: 'Carbohydrates',
    displayEs: 'Carbohidratos',
    domain: 'food',
    comparator: '<=',
    target: 275,
    unit: 'g/day',
    unitEs: 'g/día',
  },
  {
    code: '9072-0',
    system: CODE_SYSTEMS.LOINC,
    display: 'Fat',
    displayEs: 'Grasa',
    domain: 'food',
    comparator: '<=',
    target: 78,
    unit: 'g/day',
    unitEs: 'g/día',
  },
  {
    code: '82156-1',
    system: CODE_SYSTEMS.LOINC,
    display: 'Protein',
    displayEs: 'Proteína',
    domain: 'food',
    comparator: '>=',
    target: 50,
    unit: 'g/day',
    unitEs: 'g/día',
  },
  {
    code: '80489-8',
    system: CODE_SYSTEMS.LOINC,
    display: 'Caffeine',
    displayEs: 'Cafeína',
    domain: 'food',
    comparator: '<=',
    target: 400,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81133-1',
    system: CODE_SYSTEMS.LOINC,
    display: 'Fiber',
    displayEs: 'Fibra',
    domain: 'food',
    comparator: '>=',
    target: 28,
    unit: 'g/day',
    unitEs: 'g/día',
  },
  {
    code: '81021-8',
    system: CODE_SYSTEMS.LOINC,
    display: 'Cholesterol',
    displayEs: 'Colesterol',
    domain: 'food',
    comparator: '<=',
    target: 300,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '75301-2',
    system: CODE_SYSTEMS.LOINC,
    display: 'Water',
    displayEs: 'Agua',
    domain: 'food',
    comparator: '>=',
    target: 100,
    unit: 'fl oz/day',
    unitEs: 'fl oz/día',
  },
  {
    code: '81072-1',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin A',
    displayEs: 'Vitamina A',
    domain: 'food',
    comparator: '>=',
    target: 900,
    unit: 'mcg RAE/day',
    unitEs: 'mcg RAE/día',
  },
  {
    code: '81927-6',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin B1 (Thiamine)',
    displayEs: 'Vitamina B1 (Tiamina)',
    domain: 'food',
    comparator: '>=',
    target: 1.2,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81070-5',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin B2 (Riboflavin)',
    displayEs: 'Vitamina B2 (Riboflavina)',
    domain: 'food',
    comparator: '>=',
    target: 1.3,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81135-6',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin B3 (Niacin)',
    displayEs: 'Vitamina B3 (Niacina)',
    domain: 'food',
    comparator: '>=',
    target: 16,
    unit: 'mg NE/day',
    unitEs: 'mg NE/día',
  },
  {
    code: '81068-9',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin B5 (Pantothenate)',
    displayEs: 'Vitamina B5 (Pantotenato)',
    domain: 'food',
    comparator: '>=',
    target: 5,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81017-6',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin B7 (Biotin)',
    displayEs: 'Vitamina B7 (Biotina)',
    domain: 'food',
    comparator: '>=',
    target: 30,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },
  {
    code: '81066-3',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin B9 (Folate)',
    displayEs: 'Vitamina B9 (Folato)',
    domain: 'food',
    comparator: '>=',
    target: 400,
    unit: 'mcg DFE/day',
    unitEs: 'mcg DFE/día',
  },
  {
    code: '81062-2',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin B12',
    displayEs: 'Vitamina B12',
    domain: 'food',
    comparator: '>=',
    target: 2.4,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },
  {
    code: '81074-7',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin C',
    displayEs: 'Vitamina C',
    domain: 'food',
    comparator: '>=',
    target: 90,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81929-2',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin D',
    displayEs: 'Vitamina D',
    domain: 'food',
    comparator: '>=',
    target: 20,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },
  {
    code: '81076-2',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin E',
    displayEs: 'Vitamina E',
    domain: 'food',
    comparator: '>=',
    target: 15,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81078-8',
    system: CODE_SYSTEMS.LOINC,
    display: 'Vitamin K',
    displayEs: 'Vitamina K',
    domain: 'food',
    comparator: '>=',
    target: 120,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },
  {
    code: '81137-2',
    system: CODE_SYSTEMS.LOINC,
    display: 'Calcium',
    displayEs: 'Calcio',
    domain: 'food',
    comparator: '>=',
    target: 1300,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81080-4',
    system: CODE_SYSTEMS.LOINC,
    display: 'Chloride',
    displayEs: 'Cloruro',
    domain: 'food',
    comparator: '>=',
    target: 2300,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81082-0',
    system: CODE_SYSTEMS.LOINC,
    display: 'Iron',
    displayEs: 'Hierro',
    domain: 'food',
    comparator: '>=',
    target: 18,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81005-1',
    system: CODE_SYSTEMS.LOINC,
    display: 'Magnesium',
    displayEs: 'Magnesio',
    domain: 'food',
    comparator: '>=',
    target: 420,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81132-3',
    system: CODE_SYSTEMS.LOINC,
    display: 'Phosphorus',
    displayEs: 'Fósforo',
    domain: 'food',
    comparator: '>=',
    target: 1250,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81010-1',
    system: CODE_SYSTEMS.LOINC,
    display: 'Potassium',
    displayEs: 'Potasio',
    domain: 'food',
    comparator: '>=',
    target: 4700,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81011-9',
    system: CODE_SYSTEMS.LOINC,
    display: 'Sodium',
    displayEs: 'Sodio',
    domain: 'food',
    comparator: '<=',
    target: 2300,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81089-5',
    system: CODE_SYSTEMS.LOINC,
    display: 'Zinc',
    displayEs: 'Zinc',
    domain: 'food',
    comparator: '>=',
    target: 11,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81023-4',
    system: CODE_SYSTEMS.LOINC,
    display: 'Chromium',
    displayEs: 'Cromo',
    domain: 'food',
    comparator: '>=',
    target: 35,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },
  {
    code: '81027-5',
    system: CODE_SYSTEMS.LOINC,
    display: 'Copper',
    displayEs: 'Cobre',
    domain: 'food',
    comparator: '>=',
    target: 0.9,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81003-6',
    system: CODE_SYSTEMS.LOINC,
    display: 'Iodine',
    displayEs: 'Yodo',
    domain: 'food',
    comparator: '>=',
    target: 150,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },
  {
    code: '81007-7',
    system: CODE_SYSTEMS.LOINC,
    display: 'Manganese',
    displayEs: 'Manganeso',
    domain: 'food',
    comparator: '>=',
    target: 2.3,
    unit: 'mg/day',
    unitEs: 'mg/día',
  },
  {
    code: '81084-6',
    system: CODE_SYSTEMS.LOINC,
    display: 'Molybdenum',
    displayEs: 'Molibdeno',
    domain: 'food',
    comparator: '>=',
    target: 45,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },
  {
    code: '81087-9',
    system: CODE_SYSTEMS.LOINC,
    display: 'Selenium',
    displayEs: 'Selenio',
    domain: 'food',
    comparator: '>=',
    target: 55,
    unit: 'mcg/day',
    unitEs: 'mcg/día',
  },

  // Sleep — LOINC stage codes are durations, so targets are expressed as hours/night to match the
  // stored duration Observations. Light/Deep/REM defaults derive from #5862's % of total sleep time
  // applied to the 7 h sleep-duration target (50/13/20% → 3.5/0.9/1.4 h). See #5862 / #5972.
  {
    code: '93832-4',
    system: CODE_SYSTEMS.LOINC,
    display: 'Sleep duration',
    displayEs: 'Duración del sueño',
    domain: 'sleep',
    comparator: '>=',
    target: 7,
    unit: 'hours/night',
    unitEs: 'horas/noche',
  },
  {
    code: '93830-8',
    system: CODE_SYSTEMS.LOINC,
    display: 'Light sleep duration',
    displayEs: 'Duración del sueño ligero',
    domain: 'sleep',
    comparator: '>=',
    target: 3.5,
    unit: 'hours/night',
    unitEs: 'horas/noche',
  },
  {
    code: '93831-6',
    system: CODE_SYSTEMS.LOINC,
    display: 'Deep sleep duration',
    displayEs: 'Duración del sueño profundo',
    domain: 'sleep',
    comparator: '>=',
    target: 0.9,
    unit: 'hours/night',
    unitEs: 'horas/noche',
  },
  {
    code: '93829-0',
    system: CODE_SYSTEMS.LOINC,
    display: 'REM sleep duration',
    displayEs: 'Duración del sueño REM',
    domain: 'sleep',
    comparator: '>=',
    target: 1.4,
    unit: 'hours/night',
    unitEs: 'horas/noche',
  },
];

const LIFESTYLE_MEASUREMENT_DOMAINS = new Map<string, LifestyleMeasurementDomain>(
  LIFESTYLE_MEASUREMENT_CONFIGS.map((config) => [config.code, config.domain]),
);

/**
 * The lifestyle domain (`movement`/`food`/`sleep`/`mind`) for a lifestyle-measurement code, or
 * `undefined` for any other code (clinical labs/vitals/surveys). Determines the `liza-task-category`
 * a lifestyle measurement's Task carries — so it groups and themes as lifestyle everywhere — while a
 * clinical measurement stays `measurement`. Catalog codes are LOINC and globally unique.
 */
export function getLifestyleMeasurementDomain(code: string): LifestyleMeasurementDomain | undefined {
  return LIFESTYLE_MEASUREMENT_DOMAINS.get(code);
}

const LIFESTYLE_MEASUREMENT_BY_CODE = new Map<string, LifestyleMeasurementConfig>(
  LIFESTYLE_MEASUREMENT_CONFIGS.map((config) => [config.code, config]),
);

/**
 * Localized metric name for a lifestyle-measurement code, or `undefined` when `code` is not a
 * lifestyle metric (e.g. a clinical lab/vital). Returning `undefined` for non-lifestyle codes is the
 * contract that lets render-time helpers use this as a fallback without disturbing clinical labels.
 * Spanish (`lang` starting with `es`) resolves `displayEs`; anything else resolves English `display`.
 */
export function getLifestyleMeasurementDisplay(code: string, lang: string): string | undefined {
  const config = LIFESTYLE_MEASUREMENT_BY_CODE.get(code);
  if (!config) return undefined;
  return lang.startsWith('es') ? config.displayEs : config.display;
}

/**
 * Localized unit label for a lifestyle-measurement code, or `undefined` when `code` is not a
 * lifestyle metric. Spanish (`lang` starting with `es`) resolves `unitEs`; anything else the English
 * `unit`. See {@link getLifestyleMeasurementDisplay} for the non-lifestyle `undefined` contract.
 */
export function getLifestyleMeasurementUnit(code: string, lang: string): string | undefined {
  const config = LIFESTYLE_MEASUREMENT_BY_CODE.get(code);
  if (!config) return undefined;
  return lang.startsWith('es') ? config.unitEs : config.unit;
}
